import { useEffect, useEffectEvent, useRef, useState } from 'react';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';
import type { IbanValidation } from '@/types/banking';

export type IbanStatus = 'idle' | 'validating' | 'valid' | 'invalid';

interface IbanInputProps {
  id: string;
  value: string;
  onChange: (value: string) => void;
  /**
   * Checks the IBAN against the registry. The caller supplies it because staff
   * and the investor portal reach the registry by different routes and tokens.
   */
  validate: (iban: string) => Promise<IbanValidation>;
  /** What the registry says about an IBAN the user typed, once it checks out. */
  onValidated?: (validation: IbanValidation) => void;
  /**
   * What the registry says about an IBAN that was already saved. Passing it
   * also asks for that check: a form hydrating a saved account wants the field
   * marked without the user re-typing it, an empty form has nothing to check.
   */
  onHydrated?: (validation: IbanValidation) => void;
  onStatusChange?: (status: IbanStatus) => void;
  placeholder?: string;
  className?: string;
}

function statusBorder(status: IbanStatus): string {
  switch (status) {
    case 'valid':
      return 'border-success';
    case 'validating':
      return 'border-warning';
    case 'invalid':
      return 'border-destructive';
    default:
      return '';
  }
}

// Trailing spinner / tick / cross reflecting the validation status.
function StatusAdornment({ status }: { status: IbanStatus }) {
  if (status === 'validating') {
    return (
      <div className="absolute right-3 top-1/2 -translate-y-1/2">
        <div className="h-4 w-4 animate-spin rounded-full border-b-2 border-foreground" />
      </div>
    );
  }
  if (status === 'valid' || status === 'invalid') {
    return (
      <div className="absolute right-3 top-1/2 -translate-y-1/2">
        {status === 'valid' ? (
          <span className="text-success">✓</span>
        ) : (
          <span className="text-destructive">✗</span>
        )}
      </div>
    );
  }
  return null;
}

/**
 * IBAN field that checks what is typed against the bank registry on blur and
 * shows how it went, handing the caller the SWIFT, bank name and country the
 * registry resolved.
 *
 * The caller keeps the value and decides what to do with what comes back; this
 * owns only the standing of the IBAN currently in the field.
 */
export function IbanInput({
  id,
  value,
  onChange,
  validate,
  onValidated,
  onHydrated,
  onStatusChange,
  placeholder = 'NL91 ABNA 0417 1643 00',
  className,
}: IbanInputProps) {
  const [status, setStatus] = useState<IbanStatus>('idle');

  // The IBAN last checked, so a slow answer about one the user has since
  // edited is dropped instead of describing the wrong bank.
  const latestIbanRef = useRef(value);
  // A saved IBAN is checked once, and never once the user is typing their own.
  const userEditedRef = useRef(false);
  const hydratedRef = useRef(false);

  const applyStatus = (next: IbanStatus) => {
    setStatus(next);
    onStatusChange?.(next);
  };

  // Ask the registry about an IBAN and record what comes back, unless the
  // field has moved on to a different one by then.
  const recordValidation = (
    iban: string,
    onSuccess?: (validation: IbanValidation) => void
  ) => {
    latestIbanRef.current = iban;

    return validate(iban).then(
      (validation) => {
        if (latestIbanRef.current !== iban) return;

        applyStatus(validation.is_valid ? 'valid' : 'invalid');
        if (validation.is_valid) onSuccess?.(validation);
      },
      () => {
        if (latestIbanRef.current === iban) applyStatus('invalid');
      }
    );
  };

  // Nothing here should re-trigger the check - only a saved IBAN turning up
  // should - so the reads stay non-reactive.
  const checkSavedIban = useEffectEvent((iban: string) => {
    void recordValidation(iban, onHydrated);
  });

  // The saved IBAN arrives a render or two after mount, once the form it sits
  // in has loaded what it is editing.
  useEffect(() => {
    if (!onHydrated || hydratedRef.current || userEditedRef.current) return;
    if (!value.trim()) return;

    hydratedRef.current = true;
    checkSavedIban(value);
  });

  const handleChange = (next: string) => {
    userEditedRef.current = true;
    latestIbanRef.current = next;
    // Whatever was checked no longer sits in the field.
    applyStatus('idle');
    onChange(next);
  };

  const handleBlur = (iban: string) => {
    if (!iban.trim()) {
      applyStatus('idle');
      return;
    }

    applyStatus('validating');
    void recordValidation(iban, onValidated);
  };

  return (
    <div className="relative">
      <Input
        id={id}
        value={value}
        onChange={(e) => handleChange(e.target.value)}
        onBlur={(e) => handleBlur(e.target.value)}
        placeholder={placeholder}
        className={cn(statusBorder(status), className)}
      />
      <StatusAdornment status={status} />
    </div>
  );
}
